/**
 * RepresentanteFormatInfoModal - Modal with Excel format information for Representantes
 *
 * Epic 45.1: CNPJ_Cliente obrigatório, CNPJ_Hospital opcional.
 * - COM CNPJ_Hospital: Cadastra representante E vincula ao hospital
 * - SEM CNPJ_Hospital: Apenas cadastra representante (sem vínculo)
 */
import React from 'react';
import { Info, X, AlertTriangle } from 'lucide-react';

interface RepresentanteFormatInfoModalProps {
  show: boolean;
  onClose: () => void;
}

export const RepresentanteFormatInfoModal: React.FC<RepresentanteFormatInfoModalProps> = ({
  show,
  onClose,
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header - Blue */}
        <div className="flex items-center justify-between p-4 bg-blue-600 rounded-t-lg sticky top-0">
          <div className="flex items-center gap-2">
            <Info className="w-5 h-5 text-white" />
            <h3 className="text-lg font-semibold text-white">
              Formato do Excel - Representantes
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-blue-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Columns Section */}
          <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              📋 Colunas do Excel
            </p>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-600 dark:text-gray-400 w-6">A:</span>
                <span className="text-gray-800 dark:text-gray-200">
                  Email <span className="text-red-500 font-bold">*</span>
                </span>
                <span className="text-xs text-gray-500">(identificador único)</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-600 dark:text-gray-400 w-6">B:</span>
                <span className="text-gray-800 dark:text-gray-200">
                  Nome_Completo <span className="text-red-500 font-bold">*</span>
                </span>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-600 dark:text-gray-400 w-6">C:</span>
                <span className="text-gray-800 dark:text-gray-200">
                  Telefone <span className="text-red-500 font-bold">*</span>
                </span>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-600 dark:text-gray-400 w-6">D:</span>
                <span className="text-gray-800 dark:text-gray-200">
                  CNPJ_Cliente <span className="text-red-500 font-bold">*</span>
                </span>
                <span className="text-xs text-gray-500">(deve existir no sistema)</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-600 dark:text-gray-400 w-6">E:</span>
                <span className="text-gray-800 dark:text-gray-200">Codigo</span>
                <span className="text-xs text-gray-500">(opcional - referência interna)</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-600 dark:text-gray-400 w-6">F:</span>
                <span className="text-gray-800 dark:text-gray-200">CNPJ_Hospital</span>
                <span className="text-xs text-gray-500">(opcional - se informado, vincula)</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-600 dark:text-gray-400 w-6">G:</span>
                <span className="text-gray-800 dark:text-gray-200">Nome_Hospital</span>
                <span className="text-xs text-gray-500">(informativo)</span>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="text-xs text-gray-500 dark:text-gray-400">
            <span className="text-red-500 font-bold">*</span> Campos obrigatórios
          </div>

          {/* Behavior Section */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
              📋 Comportamento da Importação:
            </p>
            <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1.5 list-none">
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span><strong>COM CNPJ_Hospital:</strong> Cadastra o representante E vincula ao hospital</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 font-bold">○</span>
                <span><strong>SEM CNPJ_Hospital:</strong> Apenas cadastra o representante (sem vínculo)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 font-bold">→</span>
                <span>O Email identifica o representante (atualiza se já existir)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 font-bold">→</span>
                <span>O CNPJ_Hospital deve ser de um hospital vinculado ao cliente</span>
              </li>
            </ul>
          </div>

          {/* Multi-client info */}
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
            <p className="text-sm text-purple-800 dark:text-purple-200">
              💡 <strong>Múltiplos clientes:</strong> O arquivo pode conter representantes para
              <strong> vários clientes</strong> de uma só vez. Cada linha pode ter um CNPJ_Cliente diferente.
            </p>
          </div>

          {/* Warning */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-semibold text-amber-800 dark:text-amber-200">
                  Atenção
                </p>
                <ul className="text-sm text-amber-700 dark:text-amber-300 mt-1 space-y-1">
                  <li>• O CNPJ_Cliente deve pertencer a um cliente já cadastrado</li>
                  <li>• Se informar CNPJ_Hospital, ele deve estar vinculado ao cliente</li>
                  <li>• Um representante pode aparecer em múltiplas linhas para diferentes hospitais</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};
